
import { fetchCandidateSpots, fetchCommercialCampsites } from './overpassService';
import { fetchNVDBData } from './nvdbService';
import { fetchChargersInBounds } from './nobilService';
import { fetchWikiForPath } from './wikiService';
import { isPointNearPolyline, getDistance } from './geometryUtils';
import { getCachedSpotsInArea } from './firebaseService';
import { fetchWildlifeForRoute } from './wildlifeService';
import { Coordinates, RouteRegion, RouteStats, WikiSpot } from '../types';

const NORDIC_REGION_BOUNDS: Record<string, { latMin: number; latMax: number; lonMin: number; lonMax: number; region: RouteRegion }> = {
  denmark: { latMin: 54.5, latMax: 57.8, lonMin: 8.0,  lonMax: 15.2, region: 'Danmark' },
  finland: { latMin: 59.8, latMax: 70.1, lonMin: 20.5, lonMax: 31.6, region: 'Finland' },
  iceland: { latMin: 63.3, latMax: 66.6, lonMin: -24.5, lonMax: -13.5, region: 'Island' },
};

const SWEDEN_BOUNDS = { latMin: 55.3, latMax: 69.1, lonMin: 11.1, lonMax: 24.2 };
const NORWAY_REGION_BOUNDS = { latMin: 57.9, latMax: 71.2, lonMin: 4.5, lonMax: 31.1 };

/** Classify Swedish coordinates into Norrland / Svealand / Götaland */
const determineSwedenRegion = (lat: number): RouteRegion => {
  // Norrland: ~61.5°N and above (Gävleborg + northward)
  if (lat >= 61.5) return 'Norrland';
  // Svealand: ~58.5°N – 61.5°N (Stockholm, Uppsala, Värmland, Dalarna, etc.)
  if (lat >= 58.5) return 'Svealand';
  // Götaland: below ~58.5°N (Skåne, Halland, Göteborg, Småland, etc.)
  return 'Götaland';
};

export const determineRegion = (lat: number, lon?: number): RouteRegion => {
  if (lon !== undefined) {
    const inNorway = lat >= NORWAY_REGION_BOUNDS.latMin && lat <= NORWAY_REGION_BOUNDS.latMax
                  && lon >= NORWAY_REGION_BOUNDS.lonMin && lon <= NORWAY_REGION_BOUNDS.lonMax;
    if (!inNorway) {
      // Check Sweden first (with sub-regions)
      if (lat >= SWEDEN_BOUNDS.latMin && lat <= SWEDEN_BOUNDS.latMax
        && lon >= SWEDEN_BOUNDS.lonMin && lon <= SWEDEN_BOUNDS.lonMax) {
        return determineSwedenRegion(lat);
      }
      for (const b of Object.values(NORDIC_REGION_BOUNDS)) {
        if (lat >= b.latMin && lat <= b.latMax && lon >= b.lonMin && lon <= b.lonMax) {
          return b.region;
        }
      }
    }
  }
  if (lat >= 65.0) return 'Nord-Norge';
  if (lat >= 62.0) return 'Midt-Norge';
  return 'Sør-Norge';
};

/**
 * Detect which countries a route passes through by sampling points and using
 * a fast Nominatim reverse geocode on the start, middle, and end of the route.
 * Returns a Set of ISO country codes (lowercase): 'no', 'se', 'dk', 'fi', etc.
 */
const detectRouteCountries = async (flatCoords: [number, number][]): Promise<Set<string>> => {
  const countries = new Set<string>();
  if (flatCoords.length === 0) return countries;

  // Sample start, 25%, middle, 75%, end
  const indices = [
    0,
    Math.floor(flatCoords.length * 0.25),
    Math.floor(flatCoords.length * 0.5),
    Math.floor(flatCoords.length * 0.75),
    flatCoords.length - 1,
  ];
  const uniqueIndices = [...new Set(indices)];

  const results = await Promise.allSettled(
    uniqueIndices.map(async (idx) => {
      const [lat, lon] = flatCoords[idx];
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lon}&zoom=3`,
          { headers: { 'Accept': 'application/json', 'User-Agent': 'FricampBot/1.0 (https://fricamp.com)' } }
        );
        if (res.ok) {
          const data = await res.json();
          const cc = data.address?.country_code;
          if (cc) return cc.toLowerCase();
        }
      } catch { /* ignore */ }
      return null;
    })
  );

  for (const r of results) {
    if (r.status === 'fulfilled' && r.value) {
      countries.add(r.value);
    }
  }

  // Fallback: if Nominatim failed entirely, use bounding box heuristic
  if (countries.size === 0) {
    const avgLat = flatCoords.reduce((s, c) => s + c[0], 0) / flatCoords.length;
    const avgLon = flatCoords.reduce((s, c) => s + c[1], 0) / flatCoords.length;
    const region = determineRegion(avgLat, avgLon);
    if (['Nord-Norge', 'Midt-Norge', 'Sør-Norge'].includes(region)) countries.add('no');
    else if (['Norrland', 'Svealand', 'Götaland'].includes(region)) countries.add('se');
    else if (region === 'Danmark') countries.add('dk');
    else if (region === 'Finland') countries.add('fi');
    else if (region === 'Island') countries.add('is');
  }

  return countries;
};

export const performRouteAnalysis = async (coords: [number, number][][], onStatus?: (msg: string) => void): Promise<{ stats: RouteStats, spots: WikiSpot[] }> => {
  if (onStatus) onStatus("Analyserer fasiliteter langs ruten...");

  const flatCoords = coords.flat();
  const lats = flatCoords.map(c => c[0]);
  const lons = flatCoords.map(c => c[1]);

  const bounds = {
    latMin: Math.min(...lats) - 0.05,
    latMax: Math.max(...lats) + 0.05,
    lonMin: Math.min(...lons) - 0.05,
    lonMax: Math.max(...lons) + 0.05
  };

  const isNearAnySegment = (point: Coordinates, distance: number) => {
      return coords.some(seg => {
          const segPath = seg.map(c => ({ latitude: c[0], longitude: c[1] }));
          return isPointNearPolyline(point, segPath, distance);
      });
  }

  // Detect countries along route (Nominatim-based, works globally)
  const countries = await detectRouteCountries(flatCoords);
  const touchesNorway = countries.has('no');
  const countryList = [...countries].join(', ').toUpperCase();
  console.log(`[routeAnalysis] Land langs ruten: ${countryList || 'ukjent'}${touchesNorway ? ' — inkluderer norske datakilder' : ' — kun globale kilder'}`);

  // Timeout wrapper — don't let slow Overpass queries block the entire analysis
  const withTimeout = <T,>(promise: Promise<T>, ms: number, fallback: T): Promise<T> =>
    Promise.race([promise, new Promise<T>(resolve => setTimeout(() => resolve(fallback), ms))]);

  // --- Global sources (Overpass works everywhere) — 12s timeout ---
  const wildSpotsPromise = withTimeout(
    fetchCandidateSpots(bounds, { minDistanceHouses: 150, nearWater: false }).catch(e => {
      console.warn('[routeAnalysis] Overpass (villcamping) feilet:', e.message);
      return [] as any[];
    }), 12000, [] as any[]);
  const campsitesPromise = withTimeout(
    fetchCommercialCampsites(bounds).catch(e => {
      console.warn('[routeAnalysis] Overpass (campingplasser) feilet:', e.message);
      return [] as any[];
    }), 12000, [] as any[]);

  // --- Norwegian-only sources (NVDB, NOBIL, Firestore cached_spots) ---
  const restAreasPromise = touchesNorway
    ? fetchNVDBData(bounds).catch(e => {
        console.warn('[routeAnalysis] NVDB (norsk veidata) utilgjengelig:', e.message);
        return { objects: [] as any[] };
      })
    : Promise.resolve({ objects: [] as any[] });

  const chargersPromise = touchesNorway
    ? fetchChargersInBounds(bounds).catch(e => {
        console.warn('[routeAnalysis] NOBIL (ladestasjon) utilgjengelig:', e.message);
        return [] as any[];
      })
    : Promise.resolve([] as any[]);

  const pathCoords = flatCoords.map(c => ({ latitude: c[0], longitude: c[1] }));

  // --- Global sources ---
  const wikiPromise = fetchWikiForPath(pathCoords).catch(e => {
    console.warn('[routeAnalysis] Wikipedia feilet:', e.message);
    return [] as WikiSpot[];
  });

  // Firestore cached_spots only contains Norwegian spots — skip to avoid Firestore SDK issues
  const cachedSpotsPromise = touchesNorway
    ? getCachedSpotsInArea(bounds).catch(e => {
        console.warn('[routeAnalysis] Firestore (cached spots) feilet:', e.message);
        return [] as any[];
      })
    : Promise.resolve([] as any[]);

  const wildlifePromise = fetchWildlifeForRoute(pathCoords).catch(() => []);

  const [wildSpots, campsites, restAreasRes, chargers, wikiSpots, cachedSpots, wildlifeObs] = await Promise.all([
     wildSpotsPromise, campsitesPromise, restAreasPromise, chargersPromise, wikiPromise, cachedSpotsPromise, wildlifePromise
  ]);

  const relevantWild = wildSpots.filter(s => isNearAnySegment(s.location, 1500));
  const relevantCamps = campsites.filter(s => isNearAnySegment(s.location, 1500));
  const restAreas = (restAreasRes.objects || []).map((o: any) => ({ location: o.location }));
  const relevantRest = restAreas.filter((s: any) => isNearAnySegment(s.location, 1500));
  const relevantChargers = chargers.filter(c => isNearAnySegment(c.location, 1500));

  // Include Firestore cached spots (confirmed + high-score algorithmic)
  const relevantCached = cachedSpots
    .filter(s => s.spotType === 'confirmed' || (s.spotType === 'algorithmic' && Math.round(s.score || 0) >= 4))
    .filter(s => isNearAnySegment(s.location, 1500));

  // Deduplicate: merge cached + overpass, skip overpass spots within 50m of a cached spot
  const deduplicatedWildSpots = [...relevantCached];
  for (const overpassSpot of relevantWild) {
    const isDuplicate = deduplicatedWildSpots.some(cached =>
      getDistance(cached.location.latitude, cached.location.longitude,
                 overpassSpot.location.latitude, overpassSpot.location.longitude) < 50
    );
    if (!isDuplicate) deduplicatedWildSpots.push(overpassSpot);
  }

  // Wildlife within 300m of route
  const relevantWildlife = wildlifeObs.filter(w => isNearAnySegment(w.location, 300));

  const stats: RouteStats = {
    wildSpotsCount: deduplicatedWildSpots.length,
    campsitesCount: relevantCamps.length,
    restAreasCount: relevantRest.length,
    chargersCount: relevantChargers.length,
    attractionsCount: wikiSpots.length,
    wildlifeCount: relevantWildlife.length,
    totalSpots: deduplicatedWildSpots.length + relevantCamps.length + relevantRest.length + relevantChargers.length
  };

  return { stats, spots: wikiSpots };
};
